// Custom Hook for Sketch Management
// Organized for Supabase Migration

import { useState, useCallback, useMemo } from 'react';
import { SKETCHES_DATA } from '../data/sketchesData.js';
import { NEW_SKETCHES_DATA } from '../data/newSketchesData.js';
import { 
  exportSketchAsJSON, 
  importSketchFromJSON, 
  generatePointsFromParams,
  copyToClipboard,
  downloadFile,
  detectShapesFromImage
} from '../utils/sketchUtils.js';

export const useSketchManager = () => {
  const [selectedSketch, setSelectedSketch] = useState(0);
  const [sketchParameters, setSketchParameters] = useState({});
  const [importedSketches, setImportedSketches] = useState([]);
  const [generatedSketches, setGeneratedSketches] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showJSON, setShowJSON] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [showParameters, setShowParameters] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Combine all sketches
  const allSketches = useMemo(() => {
    return [...SKETCHES_DATA, ...NEW_SKETCHES_DATA, ...importedSketches, ...generatedSketches];
  }, [importedSketches, generatedSketches]);

  // Get current sketch
  const currentSketch = useMemo(() => {
    return allSketches[selectedSketch];
  }, [allSketches, selectedSketch]);

  // Get current parameters
  const getCurrentParams = useCallback(() => {
    return sketchParameters[selectedSketch] || currentSketch?.default_params || {};
  }, [sketchParameters, selectedSketch, currentSketch]);

  // Update parameter value
  const updateParameter = useCallback((paramName, value) => {
    setSketchParameters(prev => ({
      ...prev,
      [selectedSketch]: {
        ...prev[selectedSketch] || currentSketch?.default_params,
        [paramName]: value
      }
    }));
  }, [selectedSketch, currentSketch]);

  // Handle point drag with multiple parameters
  const handlePointDrag = useCallback((xParam, xValue, yParam, yValue) => {
    if (yParam) {
      setSketchParameters(prev => ({
        ...prev,
        [selectedSketch]: {
          ...prev[selectedSketch] || currentSketch?.default_params,
          [xParam]: xValue,
          [yParam]: yValue
        }
      }));
    } else {
      setSketchParameters(prev => ({
        ...prev,
        [selectedSketch]: {
          ...prev[selectedSketch] || currentSketch?.default_params,
          [xParam]: xValue
        }
      }));
    }
  }, [selectedSketch, currentSketch]);

  // Reset parameters to default
  const resetParameters = useCallback(() => {
    setSketchParameters(prev => ({
      ...prev,
      [selectedSketch]: currentSketch?.default_params || {}
    }));
  }, [selectedSketch, currentSketch]);

  // Export sketch to JSON
  const exportSketch = useCallback((sketch) => {
    if (!sketch) return;
    
    const currentParams = getCurrentParams();
    const sketchData = exportSketchAsJSON(sketch, currentParams);
    const filename = `${sketch.name.replace(/\s+/g, '_').toLowerCase()}_sketch.json`;
    
    downloadFile(JSON.stringify(sketchData, null, 2), filename);
  }, [getCurrentParams]);

  // Import sketch from JSON
  const importSketch = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          const importedSketch = importSketchFromJSON(jsonData);
          
          setImportedSketches(prev => [...prev, importedSketch]);
          const newIndex = allSketches.length;
          setSelectedSketch(newIndex);
          
          resolve(importedSketch);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }, [allSketches.length]);

  // Copy JSON to clipboard
  const copyJSONToClipboard = useCallback(async (sketch) => {
    if (!sketch) return false;
    
    const currentParams = getCurrentParams();
    const sketchData = exportSketchAsJSON(sketch, currentParams);
    const jsonString = JSON.stringify(sketchData, null, 2);
    
    return await copyToClipboard(jsonString);
  }, [getCurrentParams]);

  // Copy code to clipboard
  const copyCodeToClipboard = useCallback(async (code) => {
    return await copyToClipboard(code);
  }, []);

  // Analyze image and generate sketches
  const analyzeImage = useCallback(async (imageFile) => {
    setIsAnalyzing(true);
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = 400;
          canvas.height = 300;
          
          ctx.drawImage(img, 0, 0, 400, 300);
          
          const detectedShapes = detectShapesFromImage(canvas, ctx);
          setGeneratedSketches(prev => [...prev, ...detectedShapes]);
          
          if (detectedShapes.length > 0) {
            const newIndex = allSketches.length + detectedShapes.length;
            setSelectedSketch(newIndex);
          }
          
          resolve(detectedShapes);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(imageFile);
    }).finally(() => {
      setIsAnalyzing(false);
    });
  }, [allSketches.length]);

  // Handle image upload
  const handleImageUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedImage(URL.createObjectURL(file));
    
    try {
      const detectedShapes = await analyzeImage(file);
      
      if (detectedShapes.length > 0) {
        alert(`Successfully detected ${detectedShapes.length} shape(s) from the image!`);
      } else {
        alert('No recognizable geometric shapes detected in the image. Try uploading an image with clear geometric forms.');
      }
    } catch (error) {
      alert('Error analyzing image. Please try again.');
      console.error('Image analysis error:', error);
    } finally {
      event.target.value = '';
    }
  }, [analyzeImage]);

  // Group sketches by category
  const groupedSketches = useMemo(() => {
    return allSketches.reduce((acc, sketch, index) => {
      const categoryId = sketch.category_id || 'unknown';
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push({...sketch, index});
      return acc;
    }, {});
  }, [allSketches]);

  // Get sketch points
  const getSketchPoints = useCallback((sketch) => {
    if (!sketch) return [];
    const currentParams = getCurrentParams();
    return generatePointsFromParams(sketch, currentParams);
  }, [getCurrentParams]);

  // Toggle category expansion
  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategory(prev => prev === categoryId ? null : categoryId);
  }, []);

  // Select sketch
  const selectSketch = useCallback((index) => {
    setSelectedSketch(index);
  }, []);

  // Toggle JSON display
  const toggleJSON = useCallback(() => {
    setShowJSON(prev => !prev);
  }, []);

  // Toggle code display
  const toggleCode = useCallback(() => {
    setShowCode(prev => !prev);
  }, []);

  // Toggle parameters display
  const toggleParameters = useCallback(() => {
    setShowParameters(prev => !prev);
  }, []);

  return {
    // State
    selectedSketch,
    currentSketch,
    allSketches,
    groupedSketches,
    sketchParameters,
    importedSketches,
    generatedSketches,
    expandedCategory,
    showJSON,
    showCode,
    showParameters,
    uploadedImage,
    isAnalyzing,
    
    // Computed values
    getCurrentParams,
    getSketchPoints,
    
    // Actions
    setSelectedSketch,
    updateParameter,
    handlePointDrag,
    resetParameters,
    exportSketch,
    importSketch,
    copyJSONToClipboard,
    copyCodeToClipboard,
    handleImageUpload,
    toggleCategory,
    selectSketch,
    toggleJSON,
    toggleCode,
    toggleParameters
  };
};
